import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type {
  EmergencyAddress,
  EmergencyAddressInput
} from "@dialstack/sdk-webrtc";

import { US_STATES } from "constants/addresses";

import {
  e911CardTextButtonSx,
  e911ErrorSx,
  e911FieldSx,
  e911HintSx,
  e911SelectMenuPaperSx,
  e911SelectMenuSx
} from "./dialstack-softphone-widget.styles";
import { findExactMatch, formatAddress } from "./e911-address.util";

const STATE_OPTIONS = Object.entries(US_STATES.data);
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

const EMPTY_FORM = {
  address_number: "",
  street: "",
  unit: "",
  city: "",
  state: "",
  postal_code: ""
};

type Props = {
  savedAddresses: EmergencyAddress[];
  submitting: boolean;
  /** The binding's own error, e.g. the provider rejecting an unmailable address. */
  error: string | null;
  onCreate: (input: EmergencyAddressInput) => void;
  onUseExisting: (id: string) => void;
  /** Null when there are no saved addresses to go back to. */
  onCancel: (() => void) | null;
};

/**
 * Add a dispatchable 911 address.
 *
 * This reimplements the SDK's own create form, which becomes unreachable once we
 * hide `.ds-e911-root` — its body only renders while the SDK's internal `open`
 * state is true, and nothing lets us set that. Without this, a practice with
 * zero saved addresses could not place an outbound call at all.
 *
 * Plain `useState` + MUI fields rather than the app's react-hook-form +
 * `InputFormField` stack: there is no cross-field validation to express, the
 * panel is 320px of non-standard chrome that `InputFormField`'s label geometry
 * does not fit, and the softphone mounts on every page — it should not pull a
 * form context along with it.
 */
export default function E911AddressForm({
  savedAddresses,
  submitting,
  error,
  onCreate,
  onUseExisting,
  onCancel
}: Props) {
  const [form, setForm] = useState(EMPTY_FORM);

  const setField = (field: keyof typeof EMPTY_FORM, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const trimmed = Object.fromEntries(
    Object.entries(form).map(([field, value]) => [field, value.trim()])
  ) as typeof EMPTY_FORM;

  const zipLooksValid = ZIP_PATTERN.test(trimmed.postal_code);
  const canSubmit =
    Boolean(trimmed.street && trimmed.city && trimmed.state) && zipLooksValid;

  // Offer the address they already have, but never substitute it silently — a
  // typed address that differs at all may be a genuinely different place, and
  // guessing here misroutes a paramedic.
  const existing = canSubmit ? findExactMatch(trimmed, savedAddresses) : null;

  const handleSubmit = () => {
    if (!canSubmit || submitting) return;
    onCreate({
      address_number: trimmed.address_number || undefined,
      street: trimmed.street,
      // An empty string fails provider validation where an absent field passes.
      unit: trimmed.unit || undefined,
      city: trimmed.city,
      state: trimmed.state,
      postal_code: trimmed.postal_code
    });
  };

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="No."
          size="small"
          sx={[e911FieldSx, { width: 72, flexShrink: 0 }]}
          value={form.address_number}
          onChange={(event) => setField("address_number", event.target.value)}
          inputProps={{ "aria-label": "Street number" }}
        />
        <TextField
          label="Street"
          size="small"
          required
          fullWidth
          sx={e911FieldSx}
          value={form.street}
          onChange={(event) => setField("street", event.target.value)}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="Unit"
          size="small"
          sx={[e911FieldSx, { width: 96, flexShrink: 0 }]}
          value={form.unit}
          onChange={(event) => setField("unit", event.target.value)}
          helperText="Suite, floor"
        />
        <TextField
          label="City"
          size="small"
          required
          fullWidth
          sx={e911FieldSx}
          value={form.city}
          onChange={(event) => setField("city", event.target.value)}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="State"
          size="small"
          required
          select
          sx={[e911FieldSx, { width: 104, flexShrink: 0 }]}
          value={form.state}
          onChange={(event) => setField("state", event.target.value)}
          SelectProps={{
            // The closed field is 104px, so the menu's own "NY — New York"
            // label truncates to "NY — New Yor…" in it. The code is what
            // identifies the state once it is chosen; the menu keeps the full
            // name, which is what makes a 50-row list scannable.
            renderValue: (value) => value as string,
            MenuProps: {
              sx: e911SelectMenuSx,
              PaperProps: { sx: e911SelectMenuPaperSx }
            }
          }}
        >
          {STATE_OPTIONS.map(([code, name]) => (
            <MenuItem key={code} value={code} sx={{ fontSize: 12 }}>
              {code} — {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="ZIP"
          size="small"
          required
          fullWidth
          sx={e911FieldSx}
          value={form.postal_code}
          onChange={(event) => setField("postal_code", event.target.value)}
          error={Boolean(trimmed.postal_code) && !zipLooksValid}
          helperText={
            trimmed.postal_code && !zipLooksValid ? "5 or 9 digits" : undefined
          }
        />
      </Box>

      {existing && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={e911HintSx}>
            You already have {formatAddress(existing.address)}.
          </Typography>
          <Button
            type="button"
            variant="text"
            sx={e911CardTextButtonSx}
            disabled={submitting}
            onClick={() => onUseExisting(existing.id)}
          >
            Use it
          </Button>
        </Box>
      )}

      {error && (
        <Typography role="alert" sx={e911ErrorSx}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disableElevation
          disabled={!canSubmit || submitting}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: "10px" }}
        >
          {submitting ? "Saving…" : "Save and use"}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="text"
            sx={e911CardTextButtonSx}
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
